import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

import { getWhatsAppUnreadCount } from 'api/whatsapp';
import { ensureRealtimeConnected, getRealtimeSocket, onRealtimeSocketOpen } from 'api/realtime';
import { playNotificationSound } from 'utils/notificationSound';

export type WhatsAppUnreadCounts = {
  total: number;
  byInstance: Record<string, number>;
};

/**
 * "Não lida" conta todo o histórico nunca aberto nesta caixa de entrada (sem
 * corte de tempo), então é comum passar de 99 numa conta com muito
 * histórico — usar um teto baixo (99+) esconde a queda real ao abrir
 * conversas. Sobe o teto pra tornar o decremento visível na prática.
 */
export const WA_UNREAD_BADGE_MAX = 999;

export function formatWaUnreadBadge(count: number): string {
  return count > WA_UNREAD_BADGE_MAX ? `${WA_UNREAD_BADGE_MAX}+` : String(count);
}

const WhatsAppUnreadContext = createContext<WhatsAppUnreadCounts>({ total: 0, byInstance: {} });

/** Contador de não-lidas do WhatsApp (total + por instância), em tempo real. */
export function useWhatsAppUnread(): WhatsAppUnreadCounts {
  return useContext(WhatsAppUnreadContext);
}

/**
 * Mantém o contador de não-lidas do WhatsApp para a app inteira (menu lateral,
 * tabela de instâncias). Carrega via `GET /whatsapp/unread/count`, sincroniza
 * pelo evento realtime `whatsapp:unread_count` (mesmo padrão do sino de
 * notificações) e, no app desktop, toca um som a cada nova mensagem recebida.
 */
export function WhatsAppUnreadProvider({ children }: { children: ReactNode }) {
  const [counts, setCounts] = useState<WhatsAppUnreadCounts>({ total: 0, byInstance: {} });

  const refresh = useCallback(async () => {
    try {
      const data = await getWhatsAppUnreadCount();
      setCounts({ total: data?.total ?? 0, byInstance: data?.byInstance ?? {} });
    } catch {
      /* sem permissão / erro de rede → mantém o que tem */
    }
  }, []);

  useEffect(() => {
    void refresh();

    const socket = getRealtimeSocket();
    const isDesktop = typeof window !== 'undefined' && Boolean(window.desktop?.isElectron);

    const onUnread = (data: WhatsAppUnreadCounts) => {
      if (data && typeof data.total === 'number') {
        setCounts({ total: data.total, byInstance: data.byInstance ?? {} });
      }
    };
    const onMessage = (data: { direction?: string; fromMe?: boolean }) => {
      // Som só no desktop e só para mensagens RECEBIDAS (não as nossas).
      if (isDesktop && data && data.direction === 'inbound' && !data.fromMe) {
        playNotificationSound();
      }
    };

    socket.on('whatsapp:unread_count', onUnread);
    socket.on('whatsapp:message', onMessage);
    const unsubscribeOpen = onRealtimeSocketOpen(() => void refresh());
    void ensureRealtimeConnected();

    return () => {
      socket.off('whatsapp:unread_count', onUnread);
      socket.off('whatsapp:message', onMessage);
      unsubscribeOpen();
    };
  }, [refresh]);

  return <WhatsAppUnreadContext.Provider value={counts}>{children}</WhatsAppUnreadContext.Provider>;
}
